import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  Skeleton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  X as CloseIcon,
  Search as SearchIcon,
  ArrowLeft as ArrowLeftIcon,
} from 'lucide-mui';
import AnswerDisplay from './components/AnswerDisplay';
import SearchResults from './components/SearchResults';
import {
  useSearchInput,
  useSearchPopover,
  useCitationsAndThumbnails,
  useCommandSuggestions,
  useRecordingSuggestions,
} from './hooks';
import useBackendAssistantSearch from './hooks/useBackendAssistantSearch';
import useLimits from '../plan/hooks/limits';
import useUserTags from '../../hooks/useTags';
import assistantActions from './actions';
import { pushModal } from '../globalModals/globalModalsSlice';
import { Command } from './commands';
import { initializeRecorder } from '../recording/recordingSlice';
import { getFolderUrl } from '../../lib/routingUtils';
import { setDrawerState } from '../dashboard/dashboardSlice';
import { ContactSupportDialog } from '../support';
import RecordingSuggestions from './components/RecordingSuggestions';

interface AssistantToolbarProps {
  onFocusChange?: (isFocused: boolean) => void;
}

export const AssistantToolbar: React.FC<AssistantToolbarProps> = ({
  onFocusChange,
}) => {
  const { t } = useTranslation();
  // Add state for contact support dialog
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);

  // Use the new backend search hook
  const {
    status,
    results,
    error,
    isGeneratingAnswer,
    performSearch,
    reset: resetSearch,
  } = useBackendAssistantSearch();

  // Use the custom hooks
  const location = useLocation();
  const navigate = useNavigate();
  const guardLimits = useLimits();
  const { setTag } = useUserTags();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));

  // Citations and thumbnails
  const {
    activeSource,
    setActiveSource,
    fileThumbnails,
    setFileThumbnails,
    hideReferences,
    resetCitationCount,
    showVideoPlayer,
    videoPlayerFile,
  } = useCitationsAndThumbnails();

  // Update fileThumbnails when search results change
  useEffect(() => {
    if (results?.searchResults) {
      const newThumbnails = results.searchResults.map((file) => ({
        fileId: file._id,
        thumbnailUrl: file.thumbUrl || null,
        isLoading: false,
      }));
      setFileThumbnails(newThumbnails);
    }
  }, [results?.searchResults, setFileThumbnails]);

  // Citation thumbnails state for hover previews
  const [citationThumbnails, setCitationThumbnails] = useState<
    Array<{
      fileId: string;
      timestamp: string;
      thumbnailUrl: string | null;
      isLoading: boolean;
    }>
  >([]);

  // Function to fetch citation thumbnail
  const fetchCitationThumbnail = useCallback(
    (fileId: string, timestamp: string) => {
      // Check if we already have this thumbnail or are loading it
      const existing = citationThumbnails.find(
        (thumbnail) =>
          thumbnail.fileId === fileId && thumbnail.timestamp === timestamp
      );
      if (existing) return;

      // Add loading state
      setCitationThumbnails((prev) => [
        ...prev,
        {
          fileId,
          timestamp,
          thumbnailUrl: null,
          isLoading: true,
        },
      ]);

      // Note: In a real implementation, you would make an API call here
      // For now, we'll just simulate loading and use the file's thumbnail
      setTimeout(() => {
        const file = results?.searchResults?.find((f) => f._id === fileId);
        setCitationThumbnails((prev) =>
          prev.map((thumbnail) =>
            thumbnail.fileId === fileId && thumbnail.timestamp === timestamp
              ? {
                  ...thumbnail,
                  thumbnailUrl: file?.thumbUrl || null,
                  isLoading: false,
                }
              : thumbnail
          )
        );
      }, 100);
    },
    [citationThumbnails, results?.searchResults]
  );

  // Function to format timestamp
  const formatTimestamp = useCallback((seconds: string) => {
    const totalSeconds = parseFloat(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = Math.floor(totalSeconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // Search input handling
  const {
    query,
    setQuery,
    inputRef,
    handleInputChange: handleRawInputChange,
    handleKeyDown: handleRawKeyDown,
    handleClear: handleRawClear,
    updateQuery,
  } = useSearchInput();

  // Popover and UI state
  const {
    isPopoverOpen,
    setIsPopoverOpen,
    containerRef,
    searchBarWidth,
    isFileListExpanded,
    isSearchIconMode,
    isSearchExpanded,
    setIsSearchExpanded,
    toggleFileList,
    getShortcutText,
    screenWidth,
  } = useSearchPopover(results?.aiAnswer || null, {
    data: results?.searchResults || [],
    error,
  });

  // Command suggestions
  const { suggestions } = useCommandSuggestions(query);

  // Recording suggestions
  const {
    recordingSuggestions,
    isLoading: isLoadingRecordings,
  } = useRecordingSuggestions(query);

  // State for visible command cards
  const [maxVisibleCards, setMaxVisibleCards] = useState<number>(4);

  // Get placeholder text
  const getPlaceholderText = () => {
    return t('assistant.placeholder');
  };

  // Helper function to check if string is a URL
  const isURL = (str: string) => {
    const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return urlPattern.test(str);
  };

  // Handle opening the import modal for URLs
  const handleOpenImportModal = (url: string) => {
    dispatch(
      assistantActions.trackAssistantQuery({ query: `URL import: ${url}` })
    );
    dispatch(
      pushModal({
        type: 'import-urls-modal',
        url,
      })
    );
  };

  // Simplified search handler using backend
  const handleSearch = useCallback(async () => {
    if (query.trim()) {
      // Check if this is a URL
      if (isURL(query.trim())) {
        handleOpenImportModal(query.trim());
        return;
      }

      // Track the assistant query in PostHog
      dispatch(assistantActions.trackAssistantQuery({ query: query.trim() }));

      // Use guardLimits with a callback to show upgrade modal if needed
      guardLimits('assistant', async () => {
        // Set up the loading UI immediately
        setIsPopoverOpen(true);
        resetCitationCount();
        setActiveSource(null);

        // Update URL query parameter with original query
        updateQuery({ searchQuery: query.trim() });

        // Perform the search using the backend
        await performSearch(query.trim());

        // Show popover with results
        setIsPopoverOpen(true);
      });
    }
  }, [
    query,
    dispatch,
    guardLimits,
    setIsPopoverOpen,
    resetCitationCount,
    setActiveSource,
    updateQuery,
    performSearch,
  ]);

  // Adapter functions to work with the hooks API
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleRawInputChange(e);

    // If user is typing, reset search state for any future queries
    const newQuery = e.target.value;
    if (newQuery.length > 0 && status === 'succeeded') {
      resetSearch();
    }

    // If query is empty and we have results, show them
    if (
      newQuery.length === 0 &&
      (results?.aiAnswer ||
        (results?.searchResults && results.searchResults.length > 0))
    ) {
      setIsPopoverOpen(true);
    }
    // If query is empty and no results, close popover
    else if (
      newQuery.length === 0 &&
      !results?.aiAnswer &&
      (!results?.searchResults || results.searchResults.length === 0)
    ) {
      setIsPopoverOpen(false);
    }

    // If query has content, always show popover
    if (newQuery.length > 0) {
      setIsPopoverOpen(true);
    }
  };

  // Determine if the component is focused for styling purposes
  const isFocused =
    isPopoverOpen ||
    query.length > 0 ||
    (document.activeElement === inputRef.current && screenWidth < 600);

  // Handle command action
  const handleCommandAction = (command: Command) => {
    // Track the command usage
    dispatch(
      assistantActions.trackAssistantQuery({
        query: `${command.key}: ${command.command}`,
      })
    );

    // Track the command action click
    dispatch(
      assistantActions.trackAssistantAction({
        actionType: command.action,
        actionName: command.key,
        query: query.trim() || `${command.key}: ${command.command}`,
      })
    );

    // Get path info
    const { pathname } = location;
    const pathSegments = pathname.split('/').filter(Boolean);
    const isLibraryContext =
      pathSegments[0] === 'library' && pathSegments.length >= 3;
    const contextTeamId = isLibraryContext ? pathSegments[1] : null;
    const contextFolderId = isLibraryContext ? pathSegments[2] : null;

    // Close the assistant panel
    setIsPopoverOpen(false);

    // Notify parent component that assistant is no longer focused
    onFocusChange?.(false);

    // Handle different actions
    switch (command.action) {
      case 'import-from-url':
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          })
        );
        break;
      case 'record-audio':
        if (isLibraryContext && contextTeamId && contextFolderId) {
          const settings = {
            audioSettings: { microphoneAudio: true, browserAudio: true },
            videoSettings: { screen: false, webCam: false },
          };
          dispatch(
            initializeRecorder({
              teamId: contextTeamId,
              folderId: contextFolderId,
              settings,
            })
          );
        } else {
          // Redirect to library view
          navigate('/library');
        }
        break;
      case 'navigate-to-folder':
        // Handle folder navigation command with folder ID
        if (isLibraryContext && contextTeamId && command.folderId) {
          // Navigate directly to the folder using the stored folder ID
          navigate(
            getFolderUrl({
              teamId: contextTeamId,
              folderId: command.folderId,
              withBasePath: false,
            })
          );

          // Close the drawer on mobile
          if (isMobileViewport) {
            dispatch(setDrawerState('closed'));
          }
        } else {
          // Not in library context, redirect to library
          navigate('/library');
        }
        break;
      case 'record-screen':
        // Check if this is meant to be a meeting
        if (
          command.key === 'instant-meeting' ||
          command.key === 'media-record-meeting'
        ) {
          navigate('/tool/p2pmeeting');
        } else if (isLibraryContext && contextTeamId && contextFolderId) {
          // Regular screen recording with context
          const settings = {
            audioSettings: { microphoneAudio: true, browserAudio: true },
            videoSettings: { screen: true, webCam: true },
          };
          dispatch(
            initializeRecorder({
              teamId: contextTeamId,
              folderId: contextFolderId,
              settings,
            })
          );
        } else {
          // Redirect to library view
          navigate('/library');
        }
        break;
      case 'record-video':
        if (isLibraryContext && contextTeamId && contextFolderId) {
          const settings = {
            audioSettings: { microphoneAudio: true, browserAudio: true },
            videoSettings: { screen: false, webCam: true },
          };
          dispatch(
            initializeRecorder({
              teamId: contextTeamId,
              folderId: contextFolderId,
              settings,
            })
          );
        } else {
          // Redirect to library view
          navigate('/library');
        }
        break;
      case 'upload':
        // Use existing actions for uploads
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          })
        );
        break;

      case 'join-meeting':
        navigate('/settings/meetingbot');
        break;
      case 'calendar-connect':
        navigate('/settings/meetingbot');
        break;
      case 'add-team-member':
        dispatch(
          pushModal({
            type: 'invite-by-link-modal',
          })
        );
        break;
      case 'integrations':
        navigate('/settings/integrations');
        break;
      case 'mute-settings':
        navigate('/settings/recorder');
        break;
      case 'rating-settings':
        navigate('/settings/user');
        break;
      case 'recording-history':
        navigate('/library');
        break;
      // Add all settings navigation handlers
      case 'user-settings':
        navigate('/settings/user');
        break;
      case 'team-settings':
        navigate('/settings/team');
        break;
      case 'display-settings':
        navigate('/settings/display');
        break;
      case 'ai-settings':
        navigate('/settings/ai');
        break;
      case 'usage-settings':
        navigate('/settings/usage');
        break;
      case 'recorder-settings':
        navigate('/settings/recorder');
        break;
      case 'billing-settings':
        navigate('/settings/billing');
        break;
      case 'plugin-settings':
        navigate('/settings/plugin');
        break;
      case 'integrations-settings':
        navigate('/settings/integrations');
        break;
      case 'meetingbot-settings':
        navigate('/settings/meetingbot');
        break;
      case 'recovery-settings':
        navigate('/settings/recovery');
        break;
      case 'contact-support':
        setSupportDialogOpen(true);
        break;
      // Add more action handlers here
      default:
        // Default to import-from-url for commands that don't have specific implementations yet
        dispatch(
          pushModal({
            type: 'import-urls-modal',
          })
        );
        break;
    }

    // Clear the input
    setQuery('');
  };

  // Handle key press events with our specific search
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Check if input is a URL before passing to original handler
    if (e.key === 'Enter' && query.trim() && isURL(query.trim())) {
      e.preventDefault();
      handleOpenImportModal(query.trim());
      return;
    }

    // Process the first suggestion card if Enter is pressed and there are suggestions
    if (e.key === 'Enter' && suggestions.length > 0 && isPopoverOpen) {
      e.preventDefault();
      handleCommandAction(suggestions[0]);
      return;
    }

    // Otherwise use original handler
    handleRawKeyDown(e, handleSearch, () => setIsPopoverOpen(false));
  };

  // Determine if there's content to display (results, answer, or processing state)
  const isLoading = status === 'loading';
  const hasContent =
    (results?.searchResults && results.searchResults.length > 0) ||
    results?.aiAnswer ||
    status === 'failed';
  const shouldShowContent = hasContent || isLoading;

  // Determine if the popover has any actual content to show
  const hasPopoverContentToShow =
    shouldShowContent ||
    suggestions.length > 0 ||
    recordingSuggestions.length > 0 ||
    isLoadingRecordings;

  // Determine if the popover should be visible
  const shouldShowPopover = isPopoverOpen && hasPopoverContentToShow;

  // Should we round the search bar's bottom corners - only change if there's something to show
  const shouldRoundBottomCorners = !hasPopoverContentToShow || !isPopoverOpen;

  // Handle input focus with our specific state
  const handleInputFocus = () => {
    // Focus the input
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // Only open popover if there's actual content to show
    if (hasPopoverContentToShow) {
      setIsPopoverOpen(true);
    } else {
      // If no content to show, keep popover closed
      setIsPopoverOpen(false);
    }

    // If in icon mode, expand the search bar
    if (isSearchIconMode) {
      setIsSearchExpanded(true);
    }

    // Always open popover on small viewports if there's content to show
    if (screenWidth < 600 && hasPopoverContentToShow) {
      setIsPopoverOpen(true);
    }

    // Notify parent component that assistant is focused
    onFocusChange?.(true);
  };

  // Handle mouse down in popover to prevent blur and focus issues
  const handlePopoverMouseDown = (e: React.MouseEvent) => {
    // Stop propagation to prevent focus issues
    e.stopPropagation();
  };

  // Close the popover
  const handleClose = () => {
    // Don't clear the AI answer or results, just close the popover UI
    setIsPopoverOpen(false);

    // Notify parent component that assistant is no longer focused
    onFocusChange?.(false);
  };

  // Handle clear with our specific state
  const handleClear = (e: React.MouseEvent) => {
    handleRawClear(e, () => {
      // Only clear the query, but keep the popover open if there are results to show
      handleRawInputChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>);

      // On small viewports, always keep the assistant visible and focused
      if (screenWidth < 600) {
        // Keep popover open on small screens
        setIsPopoverOpen(true);
        // Keep the input focused
        if (inputRef.current) {
          inputRef.current.focus();
        }
        return;
      }

      // For larger viewports, follow the original behavior
      if (!hasContent) {
        setIsPopoverOpen(false);
        // Only notify parent that focus is lost if there's no content to display
        onFocusChange?.(false);
      }
    });
  };

  // Add focus detection for small viewports
  useEffect(() => {
    const handleFocusChange = () => {
      if (screenWidth < 600 && document.activeElement === inputRef.current) {
        // Ensure popover is open when input is focused on small viewports
        setIsPopoverOpen(true);
      }
    };

    // Listen for focus events
    if (inputRef.current) {
      inputRef.current.addEventListener('focus', handleFocusChange);
    }

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('focus', handleFocusChange);
      }
    };
  }, [screenWidth, setIsPopoverOpen]);

  // Handle back button press (for all viewports)
  const handleBackButtonPress = (
    e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>
  ) => {
    // Prevent default behavior and stop propagation to prevent jumping
    e.preventDefault();
    e.stopPropagation();

    // Close the popover
    setIsPopoverOpen(false);

    // Update the query state directly to empty string
    handleRawInputChange({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);

    // Notify parent component that assistant is no longer focused
    onFocusChange?.(false);
  };

  // Handle clearing the search history
  const handleClearHistory = useCallback(() => {
    // Clear the stored answer and results
    setTag({ key: 'assistant_last_answer', value: '' });
    setTag({ key: 'assistant_last_results', value: '' });

    // Clear the UI state
    resetSearch();

    // Close the popover if it's open
    setIsPopoverOpen(false);

    // Clear the input field
    if (inputRef.current) {
      inputRef.current.value = '';
      handleRawInputChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [setTag, resetSearch, setIsPopoverOpen, handleRawInputChange]);

  // Function to handle expanding the search bar from icon mode
  const handleSearchIconClick = useCallback(() => {
    if (isSearchIconMode && !isSearchExpanded) {
      setIsSearchExpanded(true);
      // Focus the input after expanding
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [isSearchIconMode, isSearchExpanded, setIsSearchExpanded]);

  // Use for collapsing when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isSearchIconMode &&
        isSearchExpanded &&
        !query.trim()
      ) {
        setIsSearchExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchIconMode, isSearchExpanded, query, setIsSearchExpanded]);

  // Auto-display the most relevant video when search results are shown
  useEffect(() => {
    if (results?.searchResults && results.searchResults.length > 0) {
      const firstFile = results.searchResults[0];
      if (firstFile?._id) {
        setActiveSource({ fileId: firstFile._id, timestamp: '0' });
      }
    }
  }, [results?.searchResults]);

  // Handle source click to open video in new tab
  const handleSourceClick = useCallback(
    (fileId: string, timestamp: string) => {
      // Find the file to get team ID
      const file = results?.searchResults?.find((f) => f._id === fileId);
      const teamId = (file as any)?.teamId || location.pathname.split('/')[2];

      // Split current URL by # and preserve the base part
      const currentUrl = window.location.href;
      const [baseUrl] = currentUrl.split('#');
      const url = `${baseUrl}#/library/${teamId}/recents/${fileId}?t=${timestamp}`;
      window.open(url, '_blank');
    },
    [location.pathname, results?.searchResults]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: { xs: '100px', sm: '300px' },
        maxWidth: '100%',
        mx: 0,
        ml: { xs: '8px', sm: '8px', md: '16px' },
        mr: { xs: isFocused ? 0 : '2px', sm: 0 },
        flexGrow: 1,
        display: 'flex',
        zIndex: isFocused ? 1200 : 1150, // Increase z-index when focused
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        overflowAnchor: 'none', // Prevent scroll anchoring
      }}
      ref={containerRef}
      onClick={(e) => e.stopPropagation()} // Prevent click propagation
    >
      {/* Search Icon Button (when in icon mode and not expanded) */}
      {isSearchIconMode && !isSearchExpanded ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '20px',
            backgroundColor: 'background.paper',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
          onClick={handleSearchIconClick}
        >
          <SearchIcon width={20} height={20} color="action" />
        </Box>
      ) : (
        <TextField
          placeholder={getPlaceholderText()}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          inputRef={inputRef}
          variant="outlined"
          fullWidth
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleInputFocus();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  // Enhanced mobile visibility
                  ml: { xs: 0.75, sm: 0.5 },
                  minWidth: { xs: 30, sm: 30 },
                  p: { xs: 0.25, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    color: 'rgba(0, 0, 0, 0.38)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: 18,
                    cursor: isFocused || isPopoverOpen ? 'pointer' : 'default',
                    position: 'relative',
                    overflow: 'hidden',
                    minWidth: { xs: 24, sm: 18 },
                    mr: { xs: 0.5, sm: 0 },
                  }}
                  onClick={
                    isFocused || isPopoverOpen
                      ? handleBackButtonPress
                      : undefined
                  }
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      opacity: isFocused || isPopoverOpen ? 1 : 0,
                      transform:
                        isFocused || isPopoverOpen ? 'scale(1)' : 'scale(0.8)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      visibility:
                        isFocused || isPopoverOpen ? 'visible' : 'hidden',
                    }}
                  >
                    <ArrowLeftIcon width={18} height={18} />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      opacity: isFocused || isPopoverOpen ? 0 : 1,
                      transform:
                        isFocused || isPopoverOpen ? 'scale(0.8)' : 'scale(1)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      visibility:
                        isFocused || isPopoverOpen ? 'hidden' : 'visible',
                    }}
                  >
                    <SearchIcon width={18} height={18} />
                  </Box>
                </Box>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{
                  mr: { xs: 0.5, sm: 0.75 },
                  my: 0,
                  minWidth: 'auto',
                  display: 'flex',
                  gap: 0.75,
                }}
              >
                {/* Keyboard shortcut text */}
                {!query && !isPopoverOpen && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.38)',
                      mr: 1,
                      userSelect: 'none',
                    }}
                  >
                    {getShortcutText()}
                  </Typography>
                )}
                {/* Show clear button ONLY when there's query text */}
                {query && (
                  <Box
                    component="button"
                    type="button"
                    onClick={handleClear}
                    sx={{
                      cursor: isLoading ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      width: '22px',
                      height: '22px',
                      opacity: 1,
                      borderRadius: '50%',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: 'scale(1)',
                      '&:hover': {
                        opacity: isLoading ? 1 : 1,
                        backgroundColor: isLoading ? 'transparent' : 'white',
                        transform: isLoading ? 'scale(1)' : 'scale(1.1)',
                      },
                      '&:active': {
                        transform: isLoading ? 'scale(1)' : 'scale(0.95)',
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <CloseIcon width={18} height={18} />
                    )}
                  </Box>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            width: '100%',
            position: 'relative',
            zIndex: 1125, // Above any other elements
            ...(isFocused && {
              width: '100%',
            }),
            '& .MuiOutlinedInput-root': {
              height: '40px',
              backgroundColor: 'background.paper',
              borderRadius: shouldRoundBottomCorners ? '20px' : '20px 20px 0 0',
              ...(isFocused && {
                borderRadius: {
                  xs: shouldRoundBottomCorners ? '16px' : '16px 16px 0 0',
                  sm: shouldRoundBottomCorners ? '20px' : '20px 20px 0 0',
                },
              }),
              '& fieldset': {
                border: 'none',
                boxShadow: 'none',
              },
              display: 'flex',
              alignItems: 'center',
              paddingLeft: { xs: 0.75, sm: 0.75 }, // Restore this for non-focused state
              paddingRight: { xs: 0.5, sm: 0.5 },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                opacity: 0.9,
              },
              '&.Mui-focused': {
                opacity: 0.95,
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '0px 0px 0px 8px',
              height: '40px',
              lineHeight: '40px',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              textIndent: '0',
              display: 'flex',
              alignItems: 'center',
              '&::placeholder': {
                opacity: 0.7,
                transition: 'opacity 0.2s ease',
                '& > span': {
                  fontSize: '0.75rem',
                  color: 'rgba(0, 0, 0, 0.38)',
                  marginLeft: '4px',
                },
              },
              '&:focus::placeholder': {
                opacity: 0.4,
              },
            },
            '& .MuiInputAdornment-root': {
              height: '40px',
              maxHeight: '40px',
              marginTop: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            },
          }}
        />
      )}

      {/* Replace Popover with absolute positioned Paper - only show when there is content */}
      {shouldShowPopover && (
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            width: searchBarWidth
              ? `${searchBarWidth}px`
              : { xs: '90vw', sm: '400px' },
            zIndex: 1200,
            maxHeight: '70vh',
            overflow: 'auto',
            borderRadius: '0 0 20px 20px',
            bgcolor: 'background.paper',
            border: 'none',
            marginTop: '-1px',
            opacity: 0,
            transform: 'translateY(-8px)',
            animation: 'slideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards',
            '@keyframes slideIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(-8px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
          onMouseDown={handlePopoverMouseDown}
        >
          <Box sx={{ p: 0.75 }} onMouseDown={handlePopoverMouseDown}>
            {/* Command Suggestions - show as soon as there are matches */}
            {suggestions.length > 0 && (
              <Box
                sx={{
                  mb: shouldShowContent ? 1 : 0,
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'nowrap',
                  p: 1,
                  backgroundColor: 'background.paper',
                  borderRadius: shouldShowContent ? '8px' : '0 0 8px 8px',
                  overflowX: 'hidden',
                }}
                ref={(el: HTMLDivElement | null) => {
                  if (el) {
                    // Use a simpler calculation with a fixed width per card that accounts for all spacing
                    // We'll use a more generous estimate to ensure more cards are visible
                    const containerWidth = el.offsetWidth;

                    // Use a smaller effective width per card (including gap) to show more cards
                    const effectiveCardWidth = 160; // Card + padding + part of the gap

                    // Calculate max cards with a bias toward showing more
                    const calculatedMaxCards = Math.max(
                      2,
                      Math.floor(containerWidth / effectiveCardWidth)
                    );

                    // Only update state if the value changes
                    if (calculatedMaxCards !== maxVisibleCards) {
                      setMaxVisibleCards(calculatedMaxCards);
                    }
                  }
                }}
              >
                {suggestions.slice(0, maxVisibleCards).map((suggestion) => (
                  <Box
                    key={suggestion.command}
                    sx={{
                      minWidth: '140px',
                      maxWidth: '180px',
                      height: 'auto',
                      p: 1.5,
                      cursor: 'pointer',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                    onClick={() => handleCommandAction(suggestion)}
                  >
                    {suggestion.icon && (
                      <suggestion.icon
                        width={18}
                        height={18}
                        color="text.secondary"
                      />
                    )}
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: '1.3',
                      }}
                    >
                      {suggestion.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Recording Suggestions - show matched recordings */}
            {(recordingSuggestions.length > 0 || isLoadingRecordings) && (
              <RecordingSuggestions
                recordings={recordingSuggestions}
                isLoading={isLoadingRecordings}
              />
            )}

            {/* When loading, show skeleton loader */}
            {isLoading ? (
              <Box sx={{ p: 1 }}>
                {/* Main answer skeleton */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2.5,
                  }}
                >
                  {/* Thumbnail skeleton */}
                  <Skeleton
                    variant="rectangular"
                    width={240}
                    height={135}
                    sx={{
                      borderRadius: '12px',
                      display: { xs: 'none', md: 'block' },
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    }}
                  />

                  {/* Text content skeleton */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Skeleton
                      variant="text"
                      width="90%"
                      height={20}
                      sx={{
                        mb: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width="95%"
                      height={20}
                      sx={{
                        mb: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width="85%"
                      height={20}
                      sx={{
                        mb: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width="70%"
                      height={20}
                      sx={{
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      }}
                    />
                  </Box>
                </Box>

                {/* Show search results below skeleton if they're available */}
                {results?.searchResults && results.searchResults.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <SearchResults
                      results={{ data: results.searchResults, error: null }}
                      isFileListExpanded={isFileListExpanded}
                      toggleFileList={toggleFileList}
                      fileThumbnails={fileThumbnails}
                      setFileThumbnails={setFileThumbnails}
                      handleClose={handleClose}
                      isLoading
                      query={query}
                      aiSummary={results.aiAnswer}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                {/* Only show the other content when not loading */}
                {status === 'failed' && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: '16px',
                      bgcolor: 'transparent',
                      color: 'error.dark',
                    }}
                  >
                    <Typography variant="body2">
                      {error || t('assistant.search.failed')}
                    </Typography>
                  </Paper>
                )}

                {/* AI Answer Section */}
                {(status === 'succeeded' ||
                  results?.aiAnswer ||
                  isGeneratingAnswer) && (
                  <AnswerDisplay
                    aiAnswer={results?.aiAnswer || ''}
                    isGeneratingAnswer={isGeneratingAnswer}
                    lastSearchQuery={query}
                    results={{
                      data: results?.searchResults || [],
                      error: null,
                    }}
                    featuredThumbnail={
                      results?.searchResults && results.searchResults.length > 0
                        ? (() => {
                            // Use featuredThumbnail fileId if available, otherwise first result
                            const featuredFileId = results.featuredThumbnail;
                            const featuredFile = featuredFileId
                              ? results.searchResults.find(
                                  (f) => f._id === featuredFileId
                                )
                              : results.searchResults[0];

                            return featuredFile
                              ? {
                                  fileId: featuredFile._id || '',
                                  fileName: featuredFile.name || '',
                                  timestamp: '0',
                                  thumbnailUrl: featuredFile.thumbUrl || null,
                                  isLoading: false,
                                }
                              : null;
                          })()
                        : null
                    }
                    activeSource={activeSource}
                    handleSourceClick={handleSourceClick}
                    citationThumbnails={citationThumbnails}
                    fetchCitationThumbnail={fetchCitationThumbnail}
                    formatTimestamp={formatTimestamp}
                    hideReferences={hideReferences}
                    onClearHistory={handleClearHistory}
                    showVideoPlayer={showVideoPlayer}
                    videoPlayerFile={videoPlayerFile}
                  />
                )}

                {/* Search Results Section */}
                {(status === 'succeeded' ||
                  (isGeneratingAnswer && results?.searchResults)) &&
                  results?.searchResults && (
                    <SearchResults
                      results={{ data: results.searchResults, error: null }}
                      isFileListExpanded={isFileListExpanded}
                      toggleFileList={toggleFileList}
                      fileThumbnails={fileThumbnails}
                      setFileThumbnails={setFileThumbnails}
                      handleClose={handleClose}
                      isLoading={false}
                      query={query}
                      aiSummary={results.aiAnswer}
                    />
                  )}
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {supportDialogOpen && (
        <ContactSupportDialog
          onClose={() => setSupportDialogOpen(false)}
          formSource="Assistant - Help Command"
        />
      )}
    </Box>
  );
};

export default AssistantToolbar;
